import * as THREE from "three";
import { JOINT_SIZE_PERCENTAGES } from "../joints/jointStyles";
import { readConstraintMarkerSettings } from "../../selected-constraint-visibility/markerSettingsEvents";
import { clampJointMarkerScalePercent } from "../../selected-joint-visibility";
import { computeLargestRobotDimension } from "../computeLargestRobotDimension";
import { safeTraverse } from "./sceneObjectLookups";
import {
  CONSTRAINT_MARKER_MAX_SCALE,
  CONSTRAINT_MARKER_MIN_SCALE,
  CONSTRAINT_MARKER_REFERENCE_MODEL_DIMENSION,
} from "./sceneConstants";

export function getConstraintMarkerSettingsScales(settings = readConstraintMarkerSettings()) {
  const markerScale = Math.max(0.1, Number(settings.markerScalePercent) / 100 || 1);
  const lineWidthScale = Math.max(0.1, Number(settings.lineWidthPercent) / 100 || 1);
  return {
    markerScale,
    lineWidthScale,
    anchorSpheresVisible: settings.anchorSpheresVisible !== false,
    tendonParsedAppearance: settings.tendonParsedAppearance !== false,
  };
}

export function computeConstraintMarkerScaleFromModelGroup(modelGroup) {
  if (!modelGroup) return 1;
  const largestDim = computeLargestRobotDimension(modelGroup);
  if (!Number.isFinite(largestDim) || largestDim <= 0) return 1;
  return THREE.MathUtils.clamp(
    largestDim / CONSTRAINT_MARKER_REFERENCE_MODEL_DIMENSION,
    CONSTRAINT_MARKER_MIN_SCALE,
    CONSTRAINT_MARKER_MAX_SCALE
  );
}

/** World-space pivot positions for every joint visualization group under the model. */
export function collectJointVizWorldPositions(modelGroup) {
  const positions = [];
  if (!modelGroup) return positions;
  const scratch = new THREE.Vector3();
  safeTraverse(modelGroup, (obj) => {
    if (obj?.userData?.isJointVizGroup && obj.userData?.jointUuid) {
      obj.getWorldPosition(scratch);
      positions.push(scratch.clone());
    }
  });
  return positions;
}

/** Shortest center-to-center distance between any two distinct joint pivots. */
export function globalMinPivotSeparation(positions) {
  const n = positions.length;
  if (n < 2) return Infinity;
  let dMin = Infinity;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = positions[i].distanceTo(positions[j]);
      if (d < dMin) dMin = d;
    }
  }
  return Number.isFinite(dMin) ? dMin : Infinity;
}

export function computeJointMarkerSizesFromModelGroup(modelGroup, markerScalePercent) {
  if (!modelGroup) return null;
  const largestDim = computeLargestRobotDimension(modelGroup);
  const scaleMultiplier = clampJointMarkerScalePercent(markerScalePercent) / 100;
  let centerSize = JOINT_SIZE_PERCENTAGES.center * largestDim * scaleMultiplier;
  let endpointSize = JOINT_SIZE_PERCENTAGES.endpoint * largestDim * scaleMultiplier;
  let glowSize = JOINT_SIZE_PERCENTAGES.glow * largestDim * scaleMultiplier;

  // Hard cap from global closest pair: two center spheres of radius r may touch at d=2r;
  // keep a small gap so skins do not visually merge.
  const positions = collectJointVizWorldPositions(modelGroup);
  const dMin = globalMinPivotSeparation(positions);
  const OVERLAP_SURFACE_GAP = 0.88;
  let overlapLimited = false;
  if (positions.length >= 2 && dMin > 1e-6) {
    const maxCenterRadius = (dMin / 2) * OVERLAP_SURFACE_GAP;
    if (centerSize > maxCenterRadius) {
      const pack = maxCenterRadius / centerSize;
      centerSize *= pack;
      endpointSize *= pack;
      glowSize *= pack;
      overlapLimited = true;
    }
  }

  // Minimum sizes for very small / sparse robots — but do not re-inflate past the non-overlap
  // cap on dense rigs (that was causing Shadow Hand spheres to stay huge and overlap).
  const MIN_CENTER_SIZE = 0.009; // Minimum 9mm
  const MIN_ENDPOINT_SIZE = 0.002; // Minimum 2mm
  const MIN_GLOW_SIZE = 0.02; // Minimum 2cm
  const ABS_TINY = 1e-5;

  if (overlapLimited) {
    return {
      center: Math.max(centerSize, ABS_TINY),
      endpoint: Math.max(endpointSize, ABS_TINY * 0.2),
      glow: Math.max(glowSize, ABS_TINY),
    };
  }

  return {
    center: Math.max(centerSize, MIN_CENTER_SIZE),
    endpoint: Math.max(endpointSize, MIN_ENDPOINT_SIZE),
    glow: Math.max(glowSize, MIN_GLOW_SIZE),
  };
}
